<script setup>
import { computed, defineComponent, h, ref, watch } from 'vue';

import { ElMessageBox, ElButton } from 'element-plus';
import { BookOpen } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';

import { useOlapTourLauncher } from '@/components/olapV2/state/olapTourLauncher';
import { GUIDED_TOURS, isOlapTourSeen } from '@/components/olapV2/tour/olapTourRegistry';
import PageGuide from '@/components/pageGuide/PageGuide.vue';
import { usePageGuide } from '@/composables/usePageGuide.js';

// Global "Guided tour" entry point (WP-929 / WP-987): a "?" button in the header that opens a
// modal listing the guided tours and launches the chosen one on the mounted OLAP host. The
// button lives here; visibility on OLAP pages only is driven by the launcher registry
// (isOlapActive is true only while an OLAP host is mounted). The modal is an ElMessageBox
// (h()-rendered content) per the project convention — precedent: src/pages/settings/api-keys.vue.
const { t } = useI18n();
const launcher = useOlapTourLauncher();

// "About this page": the page guide (src/config/pageGuides.js) in a drawer, so the content a
// new user saw in the empty state stays reachable once data has synced. The "?" also shows on
// non-OLAP pages that have a guide (dashboard, legacy PPC tabs, AI Actions); there it opens
// the drawer directly, since there is no tour to choose from. On OLAP pages it opens the tour
// modal as before, with "About this page" as its first row.
const { guide, hasGuide } = usePageGuide();
const aboutOpen = ref(false);
const ABOUT_ROW_ID = 'about-this-page';

// A different page means a different guide — don't leave the previous one open.
watch(guide, () => {
  aboutOpen.value = false;
});

const isVisible = computed(() => launcher.isOlapActive || hasGuide.value);

// A light nudge on the "?" itself: dotted while any runnable tour hasn't been opened yet.
const anyUnseen = computed(() =>
  GUIDED_TOURS.some((tour) => launcher.isAvailable(tour.id) && !isOlapTourSeen(tour.id)),
);

// Snapshot the catalogue at open time, each row tagged with whether it can run on the current
// page (its host is mounted). Unavailable rows are shown disabled rather than hidden, so the
// user still learns the tour exists.
const buildRows = () => [
  ...(hasGuide.value
    ? [
      {
        id: ABOUT_ROW_ID,
        icon: BookOpen,
        title: t('About this page'),
        description: t('What this page shows, the questions it answers, and how to use it.'),
        available: true,
      },
    ]
    : []),
  ...GUIDED_TOURS.map((tour) => ({
    id: tour.id,
    icon: tour.icon,
    title: tour.title,
    description: tour.description,
    available: launcher.isAvailable(tour.id),
  })),
];

const onView = (id) => {
  ElMessageBox.close();
  if (id === ABOUT_ROW_ID) {
    aboutOpen.value = true;
    return;
  }
  launcher.launch(id);
};

const GuidedTourContent = defineComponent({
  setup() {
    const rows = buildRows();
    return () =>
      h(
        'ul',
        { class: 'guided-tour-list' },
        rows.map((row) =>
          h(
            'li',
            {
              key: row.id,
              class: ['guided-tour-item', { 'guided-tour-item--disabled': !row.available }],
            },
            [
              h('span', { class: 'guided-tour-item-icon' }, [h(row.icon, { size: 20 })]),
              h('div', { class: 'guided-tour-item-body' }, [
                h('p', { class: 'guided-tour-item-title' }, row.title),
                h('p', { class: 'guided-tour-item-desc' }, row.description),
              ]),
              h(
                ElButton,
                {
                  class: 'guided-tour-item-action',
                  disabled: !row.available,
                  onClick: () => onView(row.id),
                },
                () => t('View'),
              ),
            ],
          ),
        ),
      );
  },
});

const openGuidedTour = () => {
  ElMessageBox({
    title: hasGuide.value ? t('Help') : t('Guided tour'),
    message: h(GuidedTourContent),
    showConfirmButton: false,
    showCancelButton: false,
    customClass: 'guided-tour-msgbox',
  }).catch(() => {});
};

const onHelpClick = () => {
  if (launcher.isOlapActive) {
    openGuidedTour();
    return;
  }
  aboutOpen.value = true;
};

const buttonLabel = computed(() => (hasGuide.value ? t('Help') : t('Guided tour')));
</script>

<template>
  <button
    v-if="isVisible"
    class="guided-tour-bell"
    :title="buttonLabel"
    :aria-label="buttonLabel"
    @click="onHelpClick"
  >
    <span class="guided-tour-bell-icon">
      <!-- Lucide has no bare "?" glyph (only circle/badge/… wrapped variants), so this is
           lucide's own question-mark paths (from circle-question-mark) with the circle dropped
           and the viewBox zoomed so the mark fills the button like the bell icon does. -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="5 5 14 14"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </svg>
    </span>
    <span
      v-if="anyUnseen"
      class="guided-tour-bell-dot"
    />
  </button>

  <el-drawer
    v-model="aboutOpen"
    :title="guide.title"
    size="min(520px, calc(100vw - 32px))"
    append-to-body
  >
    <PageGuide
      :entry="guide"
      compact
    />
  </el-drawer>
</template>

<!-- Matches the Notification bell button (src/components/notifications/NotificationBell.vue):
     same 36px circle, header chip background and hover, so the "?" reads as a sibling control. -->
<style scoped>
.guided-tour-bell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--sub-bg);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.guided-tour-bell:hover {
  background: var(--wp-surface-input);
}

.guided-tour-bell-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wp-text-primary);
}

.guided-tour-bell-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--wp-state-main);
  border: 2px solid var(--sub-bg);
}
</style>

<!-- ElMessageBox teleports its content to <body>, so scoped styles never reach it (the SFC
     scope id isn't applied to the teleported nodes). Author these unscoped, namespaced under
     the customClass so they don't leak — same pattern as other teleported EP surfaces. -->
<style>
.guided-tour-msgbox {
  width: 460px;
  max-width: 90vw;
}

.guided-tour-msgbox .el-message-box__btns {
  display: none;
}

.guided-tour-msgbox .el-message-box__content {
  padding-top: 4px;
}

.guided-tour-msgbox .guided-tour-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.guided-tour-msgbox .guided-tour-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 4px;
}

.guided-tour-msgbox .guided-tour-item + .guided-tour-item {
  border-top: 1px solid var(--wp-border-subtle);
}

.guided-tour-msgbox .guided-tour-item-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--wp-surface-bg-icon);
  color: var(--wp-text-accent);
}

.guided-tour-msgbox .guided-tour-item-body {
  flex: 1 1 auto;
  min-width: 0;
}

.guided-tour-msgbox .guided-tour-item-title {
  margin: 0 0 4px;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: var(--wp-text-primary);
}

.guided-tour-msgbox .guided-tour-item-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--wp-text-tertiary);
}

.guided-tour-msgbox .guided-tour-item-action {
  flex: 0 0 auto;
  align-self: center;
  margin-left: 0;
}

.guided-tour-msgbox .guided-tour-item--disabled .guided-tour-item-icon,
.guided-tour-msgbox .guided-tour-item--disabled .guided-tour-item-title,
.guided-tour-msgbox .guided-tour-item--disabled .guided-tour-item-desc {
  color: var(--wp-text-disabled);
}
</style>
